import styled from 'styled-components';
import { Book, Github, Code, MessageCircle } from 'lucide-react';

const BuildersContainer = styled.div`
    width: 100%;
    color: white;
    font-family: Very Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: relative;
    overflow: hidden;
`;

const BuildersWrapper = styled.div`
    width: 80%;
    margin: 0 auto;
    padding: 3rem 0;
    max-width: 100%;
    
    @media screen and (max-width: 768px) {
        padding: 2rem 0;
        width: 90%;
    }
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
    
    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    min-width: 0;
    
    @media screen and (max-width: 768px) {
        gap: 1.5rem;
    }
`;

const Title = styled.h2`
    font-family: Very Sans Headline, Sans Serif;
    font-size: 46px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    
    @media screen and (max-width: 768px) {
        font-size: 32px;
        line-height: 1.2;
    }
    
    .highlight {
        background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const Description = styled.p`
    color: #94A3B8;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
    max-width: 100%;

    @media screen and (max-width: 768px) {
        font-size: 14px;
        line-height: 1.5;
    }
`;

const CodeBlock = styled.div`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 1.5rem;
    font-family: 'Courier New', 'Monaco', monospace;
    font-size: 14px;
    line-height: 1.6;
    overflow-x: auto;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;

    @media screen and (max-width: 768px) {
        font-size: 11px;
        padding: 1rem;
        line-height: 1.4;
    }
`;

const CodeComment = styled.div`
    color: #6B7280;
    word-wrap: break-word;
`;

const CodeLine = styled.div`
    display: flex;
    gap: 0.5rem;
    margin: 0.25rem 0;
    flex-wrap: wrap;
    
    @media screen and (max-width: 768px) {
        gap: 0.25rem;
    }
`;

const CodeKeyword = styled.span`
    color: #C084FC;
`;

const CodeVariable = styled.span`
    color: #94A3B8;
`;

const CodeOperator = styled.span`
    color: #94A3B8;
`;

const CodeClass = styled.span`
    color: #60A5FA;
`;

const CodeString = styled.span`
    color: #34D399;
    word-break: break-all;
    
    @media screen and (max-width: 768px) {
        display: block;
        margin-left: 0.5rem;
    }
`;

const CodePunctuation = styled.span`
    color: #94A3B8;
`;

const StartButton = styled.button`
    background: #3B82F6;
    color: white;
    border: none;
    padding: 14px 32px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s ease;
    font-family: Very Sans, Sans Serif;
    font-size: 16px;
    width: fit-content;
    
    &:hover {
        background: #2563EB;
    }

    @media screen and (max-width: 768px) {
        font-size: 14px;
        padding: 12px 24px;
        align-self: flex-start;
    }
`;

const RightSection = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    min-width: 0;
    
    @media screen and (max-width: 768px) {
        gap: 1rem;
    }
`;

const FeatureCard = styled.a`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    transition: all 0.3s ease;
    text-decoration: none;
    cursor: pointer;
    min-width: 0;
    
    &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(59, 130, 246, 0.3);
        transform: translateY(-4px);
    }
    
    @media screen and (max-width: 768px) {
        padding: 1.25rem;
        gap: 0.75rem;
    }
`;

const IconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: rgba(59, 130, 246, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    @media screen and (max-width: 768px) {
        width: 40px;
        height: 40px;
    }
`;

const FeatureTitle = styled.h3`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 18px;
    color: #ffffff;
    margin: 0;
    
    @media screen and (max-width: 768px) {
        font-size: 16px;
    }
`;

const FeatureDescription = styled.p`
    color: #94A3B8;
    font-size: 14px;
    font-family: Very Sans, Sans Serif;
    margin: 0;
    line-height: 1.5;
    
    @media screen and (max-width: 768px) {
        font-size: 13px;
        line-height: 1.4;
    }
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -20%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

const Builders = () => {
    const features = [
        {
            icon: <Book size={24} color="#3B82F6" />,
            title: 'Docs',
            description: 'Comprehensive guides and API references',
            url: '/docs'
        },
        {
            icon: <Github size={24} color="#3B82F6" />,
            title: 'GitHub',
            description: 'Explore our open-source repositories',
            url: 'https://github.com/hydrix'
        },
        {
            icon: <Code size={24} color="#3B82F6" />,
            title: 'Tools',
            description: 'Libraries and tools for developers',
            url: '/sdk'
        },
        {
            icon: <MessageCircle size={24} color="#3B82F6" />,
            title: 'Chat',
            description: 'Get help from the community',
            url: 'https://discord.gg/hydrix'
        }
    ];

    const handleGoToDocs = () => {
        window.open(
            'https://ornate-gecko-119471.netlify.app/',
            '_blank',
            'noopener, noreferrer'
        );

    };

    return (
        <BuildersContainer>
            <GradientOrb1 />
            <BuildersWrapper>
                <ContentGrid>
                    <LeftSection>
                        <Title>
                            Built for <span className="highlight">Developers</span>
                        </Title>
                        
                        <Description>
                            Everything you need to build the next generation of decentralized 
                            applications. EVM-compatible, developer-friendly, and battle-tested 
                            infrastructure.
                        </Description>
                        
                        <CodeBlock>
                            <CodeComment>// Deploy on Hydrix Chain</CodeComment>
                            <CodeLine>
                                <CodeKeyword>const</CodeKeyword>
                                <CodeVariable> provider</CodeVariable>
                                <CodeOperator> = </CodeOperator>
                                <CodeKeyword>new</CodeKeyword>
                                <CodeClass> JsonRpcProvider</CodeClass>
                                <CodePunctuation>(</CodePunctuation>
                            </CodeLine>
                            <CodeLine>
                                <CodeString>  "https://chainfree.info:5010/validator/1"</CodeString>
                            </CodeLine>
                            <CodeLine>
                                <CodePunctuation>);</CodePunctuation>
                            </CodeLine>
                        </CodeBlock>
                        
                        <StartButton onClick={handleGoToDocs}>Start Building</StartButton>
                    </LeftSection>

                    <RightSection>
                        {features.map((feature, index) => (
                            <FeatureCard 
                                key={index}
                                href={feature.url}
                                target={feature.url.startsWith('http') ? '_blank' : '_self'}
                                rel={feature.url.startsWith('http') ? 'noopener noreferrer' : ''}
                            >
                                <IconWrapper>
                                    {feature.icon}
                                </IconWrapper>
                                <FeatureTitle>{feature.title}</FeatureTitle>
                                <FeatureDescription>{feature.description}</FeatureDescription>
                            </FeatureCard>
                        ))}
                    </RightSection>
                </ContentGrid>
            </BuildersWrapper>
        </BuildersContainer>
    );
};

export default Builders;